import jsPDF from 'jspdf';
import { formatCurrency } from '../utils/statisticsUtils';
import { numberToSpanishWords } from '../utils/numberToSpanishWords';

const sanitizeCurrency = (value = 0) =>
  formatCurrency(value).replace(/\u00a0/g, ' ').trim();

const buildAmountInWords = (value = 0) => {
  const numeric = Number.isFinite(Number(value)) ? Math.round(Number(value)) : 0;
  if (!numeric) {
    return 'Cero';
  }
  const words = numberToSpanishWords(numeric);
  return words
    .split(' ')
    .filter(Boolean)
    .map((word) => (word === 'y' ? 'y' : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');
};

const drawSummaryPage = (doc, header = {}, entity = {}) => {
  const marginX = 20;
  let currentY = 20;
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  if (header.updated_at_date) {
    doc.text(header.updated_at_date, marginX, currentY);
    currentY += 8;
  }

  if (header.constructora_info) {
    doc.setFontSize(8);
    const infoLines = doc.splitTextToSize(header.constructora_info, pageWidth - marginX * 2);
    infoLines.forEach((line) => {
      doc.text(line, marginX, currentY);
      currentY += 6;
    });
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  const boxHeight = 12;
  doc.rect(marginX, currentY, pageWidth - marginX * 2, boxHeight);
  doc.text(header.plan_name || '', pageWidth / 2, currentY + 8, { align: 'center' });
  currentY += boxHeight + 12;

  const entityName = entity.legal_entity_name || header.legal_entity_name || '';
  const summaryLine = `Rendición de P.E.I. y Cuotas Sociales a ${entityName} ${header.date_filter_text || ''}`.trim();
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  const summaryLines = doc.splitTextToSize(summaryLine, pageWidth - marginX * 2);
  summaryLines.forEach((line) => {
    doc.text(line, marginX, currentY);
    currentY += 6;
  });
  currentY += 4;

  doc.setFontSize(11);
  doc.text('     Cuotas Sociales recaudadas (ver planilla Anexa):', marginX, currentY);
  doc.text(sanitizeCurrency(entity?.totals?.social_fee || 0), pageWidth - marginX, currentY, {
    align: 'right',
  });
  currentY += 8;

  doc.text('     P.E.I. recaudados (ver planilla Anexa):', marginX, currentY);
  doc.text(sanitizeCurrency(entity?.totals?.pei || 0), pageWidth - marginX, currentY, {
    align: 'right',
  });
  currentY += 10;

  doc.setFont('helvetica', 'bold');
  const balanceLabelX = pageWidth - marginX - 80;
  doc.text('Saldo a rendir:', balanceLabelX, currentY, { align: 'left' });
  doc.text(sanitizeCurrency(entity?.totals?.total || 0), pageWidth - marginX, currentY, {
    align: 'right',
  });
  currentY += 14;

  doc.setFont('helvetica', 'normal');
  doc.text(`Son pesos: ${buildAmountInWords(entity?.totals?.total)} pesos`, marginX, currentY);
  currentY += 14;

  const boxY = currentY;
  const boxWidth = pageWidth - marginX * 2;
  doc.rect(marginX, boxY, boxWidth, 12);
  doc.setFont('helvetica', 'bold');
  doc.text('Sirva el presente de suficiente RECIBO.-', pageWidth / 2, boxY + 8, { align: 'center' });
  currentY += 42;

  const signatureY = currentY;
  const signatureWidth = (pageWidth - marginX * 2 - 40) / 2;
  const leftLineStart = marginX + 5;
  const leftLineEnd = leftLineStart + signatureWidth;
  const rightLineStart = leftLineEnd + 30;
  const rightLineEnd = rightLineStart + signatureWidth;

  doc.setDrawColor(0);
  doc.line(leftLineStart, signatureY, leftLineEnd, signatureY);
  doc.line(rightLineStart, signatureY, rightLineEnd, signatureY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Por Administrcion', leftLineStart, signatureY + 10);
  doc.text(header.plan_name || '', leftLineStart, signatureY + 16);

  doc.text('Recibí Conforme', rightLineStart, signatureY + 10);
  const entitySignatureLines = doc.splitTextToSize(entityName, signatureWidth);
  entitySignatureLines.forEach((line, index) => {
    doc.text(line, rightLineStart, signatureY + 16 + index * 6);
  });
};

const DETAIL_COLUMNS = [
  { key: 'contract_number', label: 'Orden', width: 14, align: 'center' },
  { key: 'subscriber_name', label: 'Apellido y Nombre', width: 70, align: 'left' },
  { key: 'fee_number1', label: 'Cuota Simple', width: 20, align: 'center' },
  { key: 'fee_number2', label: 'Cuota Doble', width: 20, align: 'center' },
  { key: 'cancellation_date', label: 'Fecha Pago', width: 20, align: 'center' },
  { key: 'social_fee_amount', label: 'Cuota Social', width: 25, align: 'right', currency: true },
  { key: 'pei_amount', label: 'P.E.I.', width: 25, align: 'right', currency: true },
];

const formatCellValue = (row, column) => {
  if (column.currency) {
    return sanitizeCurrency(row[column.key] || 0);
  }
  const value = row[column.key];
  if (value === null || value === undefined) {
    return '';
  }
  return `${value}`;
};

const renderDetailPages = (doc, header = {}, entity = {}) => {
  const groups = entity.groups || [];
  if (!groups.length) {
    return;
  }

  const marginX = 8;
  const marginY = 16;
  let currentY = marginY;
  let pageHeight = doc.internal.pageSize.getHeight();

  const printDetailHeader = () => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    if (header.updated_at_date) {
      doc.text(header.updated_at_date, marginX, currentY);
      currentY += 7;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Presenta el siguiente detalle', marginX, currentY);
    currentY += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
  };

  const ensureSpace = (neededHeight) => {
    if (currentY + neededHeight > pageHeight - 20) {
      doc.addPage('landscape');
      pageHeight = doc.internal.pageSize.getHeight();
      currentY = marginY;
      printDetailHeader();
      return true;
    }
    return false;
  };

  const drawTableHeader = (skipSpaceCheck = false) => {
    const headerHeight = 8;
    if (!skipSpaceCheck) {
      ensureSpace(headerHeight + 6);
    }
    let columnX = marginX;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    DETAIL_COLUMNS.forEach((column) => {
      doc.rect(columnX, currentY, column.width, headerHeight);
      doc.text(column.label, columnX + 2, currentY + 6);
      columnX += column.width;
    });
    doc.setFont('helvetica', 'normal');
    currentY += headerHeight;
  };

  const truncateText = (text, maxChars = 32) => {
    if (!text) {
      return '';
    }
    return text.length > maxChars ? `${text.substring(0, maxChars - 3)}...` : text;
  };

  const drawCouponRow = (coupon) => {
    doc.setFontSize(7);
    const columnData = DETAIL_COLUMNS.map((column) => {
      let rawValue = formatCellValue(coupon, column);
      if (column.key === 'subscriber_name') {
        rawValue = truncateText(rawValue, 50);
      }
      const lines = doc.splitTextToSize(rawValue || '', column.width - 2);
      return {
        lines: lines.length ? lines : [''],
        align: column.align,
        width: column.width,
      };
    });

    const maxLines = Math.max(...columnData.map((cell) => cell.lines.length));
    const lineSpacing = 4.2;
    const rowHeight = maxLines * lineSpacing + 1.5;

    const pageBroken = ensureSpace(rowHeight + 2);
    if (pageBroken) {
      drawTableHeader(true);
    }
    let columnX = marginX;
    columnData.forEach((cell, index) => {
      const column = DETAIL_COLUMNS[index];
      doc.rect(columnX, currentY, column.width, rowHeight);
      cell.lines.forEach((line, lineIndex) => {
        const textBlockHeight = cell.lines.length * lineSpacing;
        const offsetY = (rowHeight - textBlockHeight) / 2;
        const lineY = currentY + offsetY + (lineIndex + 0.8) * lineSpacing;
        if (cell.align === 'right') {
          doc.text(line, columnX + column.width - 2, lineY, { align: 'right' });
        } else if (cell.align === 'center') {
          doc.text(line, columnX + column.width / 2, lineY, { align: 'center' });
        } else {
          doc.text(line, columnX + 2, lineY);
        }
      });
      columnX += column.width;
    });
    currentY += rowHeight;
  };

  const drawGroupTotals = (groupTotals = {}) => {
    doc.setFontSize(7);
    const totalsHeight = 6;
    const pageBroken = ensureSpace(totalsHeight + 2);
    if (pageBroken) {
      drawTableHeader(true);
    }
    let columnX = marginX;
    DETAIL_COLUMNS.forEach((column) => {
      doc.rect(columnX, currentY, column.width, totalsHeight);
      if (column.currency) {
        const value = groupTotals[column.key];
        if (value !== undefined) {
          doc.setFont('helvetica', 'bold');
          const text = sanitizeCurrency(value || 0);
          const textY = currentY + totalsHeight / 2 + 0.5;
          doc.text(text, columnX + column.width - 2, textY, { align: 'right' });
          doc.setFont('helvetica', 'normal');
        }
      }
      columnX += column.width;
    });
    currentY += totalsHeight;
  };

  doc.addPage('landscape');
  pageHeight = doc.internal.pageSize.getHeight();
  currentY = marginY;
  printDetailHeader();

  groups.forEach((group) => {
    ensureSpace(18);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(`GRUPO ${group.group_name}`, marginX, currentY);
    currentY += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);

    drawTableHeader();

    (group.coupons || []).forEach((coupon) => drawCouponRow(coupon));
    drawGroupTotals({
      social_fee_amount: group?.totals?.social_fee,
      pei_amount: group?.totals?.pei,
    });
    currentY += 6;
  });
};

export const useIntermediateEntitiesPDF = () => {
  const exportIntermediateCollectionPDF = ({
    header = {},
    entities = [],
    filename = 'RecaudacionEntidadesIntermedias',
  } = {}) => {
    if (!Array.isArray(entities) || !entities.length) {
      throw new Error('No hay datos disponibles para exportar.');
    }

    try {
      const doc = new jsPDF('portrait');
      entities.forEach((entity, index) => {
        if (index > 0) {
          doc.addPage('portrait');
        }
        drawSummaryPage(doc, header, entity);
        renderDetailPages(doc, header, entity);
      });

      doc.save(`${filename}.pdf`);
    } catch (error) {
      console.error('Error exporting intermediate entities PDF:', error);
      throw new Error('Error al generar el PDF de recaudación.');
    }
  };

  return {
    exportIntermediateCollectionPDF,
  };
};
